import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AdminSidebar, Icon } from '../../components/shared';
import api from '../../services/api';
import toast from 'react-hot-toast';

const EMPTY = {
  descricao:'', descricao_detalhada:'', categoria_id:'', subcategoria_id:'',
  codigo_erp:'', codigo_ean:'', unidade_medida:'UN', preco_venda:'', preco_promocional:'',
  status:'ativo', destaque: false, ordem: 0, passo_gramas: '',
};

export default function AdminProdutoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = !!id;
  const voltarPara = location.state?.from || '/admin/produtos';

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcats] = useState([]);
  const [form, setForm]             = useState(EMPTY);
  const [produtoOriginal, setProdutoOriginal] = useState(null);
  const [fotoFile, setFoto]         = useState(null);
  const [loading, setLoading]       = useState(editing);
  const [saving, setSaving]         = useState(false);

  const carregarSubcats = (categoria_id) => {
    if (!categoria_id) { setSubcats([]); return; }
    api.get('/subcategorias', { params: { categoria_id } })
      .then(r => setSubcats(r.data))
      .catch(() => setSubcats([]));
  };

  useEffect(() => {
    api.get('/categorias').then(r => setCategorias(r.data)).catch(() => {});
    if (editing) {
      setLoading(true);
      api.get(`/produtos/${id}`)
        .then(r => {
          setForm({ ...r.data, preco_venda: r.data.preco_venda });
          setProdutoOriginal(r.data);
          carregarSubcats(r.data.categoria_id);
        })
        .catch(() => { toast.error('Produto não encontrado'); navigate(voltarPara); })
        .finally(() => setLoading(false));
    }
  }, [id]);

  const F = (k, v) => {
    setForm(p => {
      const next = { ...p, [k]: v };
      if (k === 'categoria_id') {
        next.subcategoria_id = '';
        carregarSubcats(v);
      }
      if (k === 'unidade_medida' && v !== 'KG') {
        next.passo_gramas = '';
      }
      return next;
    });
  };

  const salvar = async () => {
    if (!form.descricao) { toast.error('Descrição é obrigatória'); return; }
    if (!form.categoria_id) { toast.error('Categoria é obrigatória'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) fd.append(k, v); });
      if (fotoFile) fd.append('foto', fotoFile);
      if (editing) { await api.put(`/produtos/${id}`, fd); toast.success('Produto atualizado!'); }
      else          { await api.post('/produtos', fd); toast.success('Produto criado!'); }
      navigate(voltarPara);
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const API = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

  return (
    <div className="admin-layout">
      <AdminSidebar perfil="admin_empresa" />
      <div className="main-content">
        <div className="topbar">
          <h1>{editing ? 'Editar Produto' : 'Novo Produto'}</h1>
          <div className="topbar-actions">
            <button className="btn btn-secondary" onClick={() => navigate(voltarPara)}>Cancelar</button>
            <button className="btn btn-primary" onClick={salvar} disabled={saving || loading}>
              {saving ? 'Salvando...' : 'Salvar Produto'}
            </button>
          </div>
        </div>
        <div className="page-content">
          {loading ? <div className="loading-center"><div className="spinner" /></div> : (
            <div className="table-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '900px' }}>
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Descrição *</label>
                  <input className="form-control" value={form.descricao} onChange={e => F('descricao', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Categoria *</label>
                  <select className="form-control" value={form.categoria_id} onChange={e => F('categoria_id', e.target.value)}>
                    <option value="">Selecione...</option>
                    {categorias.map(c => <option key={c.id} value={c.id}>{c.descricao}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Subcategoria</label>
                  <select
                    className="form-control"
                    value={form.subcategoria_id}
                    onChange={e => F('subcategoria_id', e.target.value)}
                    disabled={!form.categoria_id || subcategorias.length === 0}
                  >
                    <option value="">
                      {!form.categoria_id ? 'Selecione uma categoria primeiro' : subcategorias.length === 0 ? 'Nenhuma subcategoria' : 'Nenhuma (opcional)'}
                    </option>
                    {subcategorias.map(s => <option key={s.id} value={s.id}>{s.descricao}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Preço de Venda *</label>
                  <input className="form-control" type="number" step="0.01" min="0" value={form.preco_venda}
                    onChange={e => F('preco_venda', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Preço Promocional <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(opcional)</span></label>
                  <input className="form-control" type="number" step="0.01" min="0"
                    placeholder="Deixe vazio para sem promoção"
                    value={form.preco_promocional}
                    onChange={e => F('preco_promocional', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Unidade de Medida</label>
                  <select className="form-control" value={form.unidade_medida} onChange={e => F('unidade_medida', e.target.value)}>
                    {['UN','KG','G','L','ML','PCT','CX'].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
                {form.unidade_medida === 'KG' && (
                  <div className="form-group">
                    <label className="form-label">Vender por fração</label>
                    <select className="form-control" value={form.passo_gramas || ''} onChange={e => F('passo_gramas', e.target.value)}>
                      <option value="">Quantidade em KG inteiro</option>
                      <option value="100">A cada 100g (0,100 kg)</option>
                      <option value="500">A cada 500g (0,500 kg)</option>
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Código ERP</label>
                  <input className="form-control" value={form.codigo_erp} onChange={e => F('codigo_erp', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Código EAN</label>
                  <input className="form-control" value={form.codigo_ean} onChange={e => F('codigo_ean', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Ordem</label>
                  <input className="form-control" type="number" value={form.ordem} onChange={e => F('ordem', e.target.value)} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Descrição Detalhada</label>
                <textarea className="form-control" rows={3} value={form.descricao_detalhada}
                  onChange={e => F('descricao_detalhada', e.target.value)} />
              </div>

              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '.875rem' }}>
                  <input type="checkbox" checked={!!form.destaque} onChange={e => F('destaque', e.target.checked)} />
                  ⭐ Produto em Destaque
                </label>
                <div className="form-group" style={{ minWidth: '140px' }}>
                  <label className="form-label">Status</label>
                  <select className="form-control" value={form.status} onChange={e => F('status', e.target.value)}>
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label className="form-label" style={{ margin: 0 }}>Foto do Produto</label>
                  <a
                    href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(form.codigo_ean?.length === 13 ? form.codigo_ean : form.descricao || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '.78rem', color: '#4285F4', fontWeight: 600,
                      textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    🔍 Buscar no Google Imagens
                  </a>
                </div>
                <div className="upload-area" onClick={() => document.getElementById('foto-input').click()}>
                  {fotoFile
                    ? <img src={URL.createObjectURL(fotoFile)} alt="" style={{ maxHeight: 120, borderRadius: 8 }} />
                    : produtoOriginal?.foto_url
                      ? <img src={`${API}${produtoOriginal.foto_url}`} alt="" style={{ maxHeight: 120, borderRadius: 8 }} />
                      : <><Icon name="image" size={28} /><p style={{ marginTop: '8px', color: 'var(--text-muted)', fontSize: '.85rem' }}>Clique para selecionar</p></>
                  }
                </div>
                <input id="foto-input" type="file" accept="image/*" hidden onChange={e => setFoto(e.target.files[0])} />
              </div>

              <div className="form-actions">
                <button className="btn btn-secondary" onClick={() => navigate(voltarPara)}>Cancelar</button>
                <button className="btn btn-primary" onClick={salvar} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar Produto'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
